/**
 * Alerts page — Alert Centre with notification + email preference toggles.
 * Prototype source: data-page="alerts". Local state only; Firestore wiring in Phase 4.
 * British English.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { useSession } from '../context/SessionContext';
import { api } from '../lib/api';
import {
  isPushSupported,
  isIOSWithoutInstall,
  getExistingPushToken,
  requestPushPermissionAndToken,
  unregisterPushToken,
  subscribeToMarketingTopic,
  unsubscribeFromMarketingTopic,
} from '../lib/push';
import './Alerts.css';

interface PrefRowProps {
  icon: React.ReactNode;
  name: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}

function PrefRow({ icon, name, description, checked, onChange, label }: PrefRowProps) {
  return (
    <div className="itk-pref-row">
      <span className="itk-pref-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="itk-pref-text">
        <div className="itk-pref-name">{name}</div>
        <div className="itk-pref-desc">{description}</div>
      </div>
      <button
        type="button"
        className="itk-switch"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}

export function Alerts() {
  const navigate = useNavigate();
  const session = useSession();
  const [notifications, setNotifications] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // §9.4 — the push token for the current device (set when notifications is
  // toggled on or auto-registered on mount). Null = not yet registered / push
  // unsupported / user denied permission.
  const [pushToken, setPushToken] = useState<string | null>(null);

  // Hydrate alert preferences from the BFF on mount.
  useEffect(() => {
    api
      .get<{ alertPreferences: { notifications: boolean; email: boolean; marketing?: boolean } }>('/api/v1/settings')
      .then((data) => {
        setNotifications(data.alertPreferences.notifications);
        setEmailAlerts(data.alertPreferences.email);
        setMarketing(data.alertPreferences.marketing ?? false);
        // §9.4 — adopt the existing push token if notifications is already on.
        // getExistingPushToken NEVER prompts (returns a token only when
        // permission is already granted) so page mount cannot trigger a
        // non-gesture prompt that Chrome/Edge would suppress/embargo.
        if (data.alertPreferences.notifications && isPushSupported()) {
          void getExistingPushToken().then((token) => {
            if (token) {
              setPushToken(token);
              // If marketing is already on, also subscribe this token.
              if (data.alertPreferences.marketing) void subscribeToMarketingTopic(token);
            }
          });
        }
      })
      .catch(() => {});
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const handleNotifications = async (on: boolean) => {
    if (on && isPushSupported()) {
      // Prompt within the user gesture (toggle onClick). Toast AFTER the
      // outcome so we can say "blocked in browser settings" when denied.
      const result = await requestPushPermissionAndToken();
      if (result.status === 'granted') {
        setNotifications(true);
        setPushToken(result.token);
        if (marketing) void subscribeToMarketingTopic(result.token);
        api.put('/api/v1/settings/alerts', { notifications: true, email: emailAlerts, marketing }).catch(() => {});
        showToast('Notifications on');
      } else if (result.status === 'denied') {
        setNotifications(false);
        showToast('Notifications blocked — enable them in your browser settings');
      } else if (result.status === 'unsupported') {
        setNotifications(false);
        showToast('Notifications not supported on this device');
      } else {
        setNotifications(false);
        showToast('Could not turn on notifications');
      }
      return;
    }

    setNotifications(on);
    showToast(`Notifications ${on ? 'on' : 'off'}`);
    api.put('/api/v1/settings/alerts', { notifications: on, email: emailAlerts, marketing }).catch(() => {});
    if (!on && pushToken) {
      void unregisterPushToken(pushToken);
      setPushToken(null);
    }
  };

  const handleEmail = (on: boolean) => {
    setEmailAlerts(on);
    showToast(`Email alerts ${on ? 'on' : 'off'}`);
    api.put('/api/v1/settings/alerts', { notifications, email: on, marketing }).catch(() => {});
  };

  const handleMarketing = async (on: boolean) => {
    setMarketing(on);
    showToast(`Marketing ${on ? 'on' : 'off'}`);
    api.put('/api/v1/settings/alerts', { notifications, email: emailAlerts, marketing: on }).catch(() => {});
    // §9.4 — subscribe/unsubscribe the device token from the `marketing` FCM
    // topic so Firebase Console composer pushes reach only opted-in devices.
    if (pushToken) {
      if (on) {
        void subscribeToMarketingTopic(pushToken);
      } else {
        void unsubscribeFromMarketingTopic(pushToken);
      }
    } else if (on && isPushSupported()) {
      // Marketing opt-in but no token yet — prompt within the gesture, then
      // subscribe on success.
      const result = await requestPushPermissionAndToken();
      if (result.status === 'granted') {
        setPushToken(result.token);
        void subscribeToMarketingTopic(result.token);
      } else if (result.status === 'denied') {
        showToast('Notifications blocked — enable them in your browser settings');
      }
    }
  };

  const emailDesc = session.email
    ? `Alerts sent to ${session.email}.`
    : 'Alerts sent to your inbox.';

  return (
    <>
      <TopNav title="Alert Centre" onBack={() => navigate('/')} />

      <div className="itk-wrap itk-wrap--narrow">
        <div className="itk-page-head">
          <div className="itk-overline">Alerts</div>
          <h1 className="itk-page-title">Alert Centre</h1>
          <p className="itk-sec-desc">
            Get funding alerts, inventor tips and news about new features by notification and email.
            Choose how you&rsquo;d like to receive your alerts below.
          </p>
        </div>

        <section className="itk-section-box" aria-label="Service notifications">
          <div className="itk-overline">Service notifications</div>
          <h2 className="itk-sec-title">Booking and idea alerts</h2>
          <p className="itk-sec-desc" style={{ marginBottom: 16 }}>
            Reminders about your upcoming review call and nudges to finish entering your idea. These are
            part of the service and are on by default.
          </p>
          <PrefRow
            name="Notifications"
            description={
              isIOSWithoutInstall()
                ? 'Add this site to your Home Screen for push notifications on iPhone/iPad. Until then, reminders go by email.'
                : 'In-app and push notifications on your devices.'
            }
            checked={notifications}
            onChange={handleNotifications}
            label="Notifications"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            }
          />
          <PrefRow
            name="Email"
            description={emailDesc}
            checked={emailAlerts}
            onChange={handleEmail}
            label="Email alerts"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
            }
          />
        </section>

        <section className="itk-section-box" aria-label="Marketing">
          <div className="itk-overline">Marketing</div>
          <h2 className="itk-sec-title">News and offers</h2>
          <p className="itk-sec-desc" style={{ marginBottom: 16 }}>
            Occasional emails about products and services that may interest you. This is optional and off
            by default — you can opt in here and opt out at any time.
          </p>
          <PrefRow
            name="Marketing emails"
            description="News, tips and offers from Innovate Design. You can unsubscribe at any time."
            checked={marketing}
            onChange={handleMarketing}
            label="Marketing emails"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13" />
                <path d="M22 2 15 22l-4-9-9-4 20-7z" />
              </svg>
            }
          />
        </section>
      </div>

      {toast && (
        <div className="itk-toast itk-toast--show" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </>
  );
}